import { apiClient } from "./client";
import type { ValueKind } from "./types";

// Release 1.10.0 W6 (O-R137, O-R139): VIPER for Logistics and Purchasing — the return-email extraction helper and the
// person's VIPER action notices. VIPER suggests and proposes; a person applies, accepts or dismisses.

export type ViperReturnField = "carrierCode" | "trackingNumber" | "shipDate" | "promisedReturnDate";

export interface ViperReturnSuggestion {
  field: ViperReturnField | string;
  value: string;
  valid: boolean;
  problem: string | null;
  valueKind: ValueKind;
  aiAuditId: string | null;
}

export interface ViperReturnExtraction {
  requestId: number;
  suggestions: ViperReturnSuggestion[];
  byViper: boolean;
  aiAuditId: string | null;
  notice: string;
}

export interface ViperActionButton {
  kind: "open" | "sign" | "draft-email" | string;
  label: string;
  href: string;
  draftSubject: string | null;
  draftBody: string | null;
}

export interface ViperActionNotice {
  noticeId: number;
  signal: string;
  subjectKey: string;
  title: string;
  text: string;
  writtenByViper: boolean;
  proposedAction: string;
  button: ViperActionButton;
  decision: "proposed" | "accepted" | "dismissed" | string;
  decidedBy: string | null;
  decidedAtUtc: string | null;
  dismissReason: string | null;
  createdAtUtc: string;
  snoozedUntilUtc: string | null;
  valueKind: ValueKind;
}

/** The most text the extraction helper accepts (UTF-8 bytes). */
export const MAX_EMAIL_BYTES = 8 * 1024;

export function utf8Bytes(text: string): number {
  return new TextEncoder().encode(text).length;
}

export const viperLogisticsApi = {
  extractReturn: async (requestId: number, text: string): Promise<ViperReturnExtraction> =>
    (await apiClient.post<ViperReturnExtraction>(`/logistics/requests/${requestId}/extract-return`, { text })).data,
  actions: async (): Promise<{ enabled: boolean; items: ViperActionNotice[] }> =>
    (await apiClient.get<{ enabled: boolean; items: ViperActionNotice[] }>("/viper/actions")).data,
  accept: async (noticeId: number): Promise<ViperActionNotice> =>
    (await apiClient.post<ViperActionNotice>(`/viper/actions/${noticeId}/accept`)).data,
  dismiss: async (noticeId: number, reason: string): Promise<ViperActionNotice> =>
    (await apiClient.post<ViperActionNotice>(`/viper/actions/${noticeId}/dismiss`, { reason })).data
};

/** A mailto: link that opens the person's own email client with VIPER's draft; nothing is sent by Summit Operations. */
export function draftMailto(button: ViperActionButton, to = ""): string | null {
  if (button.kind !== "draft-email" || !button.draftSubject) return null;
  const params = new URLSearchParams({ subject: button.draftSubject, body: button.draftBody ?? "" });
  return `mailto:${encodeURIComponent(to)}?${params.toString().replace(/\+/g, "%20")}`;
}
